import Bowser from "bowser"
import * as Sentry from "@sentry/react"
import {EMPTY, defer, from} from "rxjs"
import {catchError, filter} from "rxjs/operators"

const UNKNOWN = "unknown"

export const BROWSER_KEYS = {
  CHROME: "chrome",
  FIREFOX: "firefox",
  SAFARI: "safari",
  EDGE: "edge",
  OPERA: "opera",
  SAMSUNG: "samsung",
  UNKNOWN
}

// First substring match wins; order matters since Edge and Samsung are Chromium-based
const BROWSER_NAME_RULES = [
  [BROWSER_KEYS.EDGE, "edge"],
  [BROWSER_KEYS.SAMSUNG, "samsung"],
  [BROWSER_KEYS.OPERA, "opera"],
  [BROWSER_KEYS.FIREFOX, "firefox"],
  [BROWSER_KEYS.CHROME, "chrom"],
  [BROWSER_KEYS.SAFARI, "safari"]
]

// Heuristic detection of in-app webviews, which restrict getUserMedia
const WEB_VIEW_RULES = [
  ["facebook", /FBAN|FBAV|FB_IAB/i],
  ["instagram", /Instagram/i],
  ["tiktok", /TikTok|musical_ly|BytedanceWebview/i],
  ["whatsapp", /WhatsApp/i],
  ["linkedin", /LinkedInApp/i],
  ["wechat", /MicroMessenger/i],
  ["android-webview", /;\s*wv\)/i]
]

const resolveBrowserKey = (lowercasedName = "") =>
    BROWSER_NAME_RULES.find(([, token]) => lowercasedName.includes(token))?.[0] ?? UNKNOWN

const resolveWebViewHost = (userAgent = "") =>
    WEB_VIEW_RULES.find(([, pattern]) => pattern.test(userAgent))?.[0] ?? null

// "120" aggregates in Elastic, "120.0.6099.119" does not
const toMajorVersion = (version) => {
  const [major] = String(version ?? "").split(".")
  return /^\d+$/.test(major) ? major : null
}

// Accessing window[name] can itself throw in some privacy modes, so it stays inside the try
const isStorageAvailable = (win, name) => {
  try {
    const storage = win[name]
    const testKey = "__storage_test__"
    storage.setItem(testKey, testKey)
    storage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

// Both success and failure paths spread this, so the shape is invariant
const DEFAULT_DEVICE_INFO = {
  userAgent: null,
  platform: UNKNOWN,
  language: null,
  os: UNKNOWN,
  osVersion: UNKNOWN,
  engine: UNKNOWN,
  browserInfo: {key: BROWSER_KEYS.UNKNOWN, name: null, version: null, majorVersion: null},
  webViewHost: null,
  isFramed: false,
  features: {
    localStorage: false,
    sessionStorage: false,
    touchEnabled: false,
    geolocationSupported: false,
    mediaDevices: false,
    secureContext: false
  },
  parseFailed: false
}

// Derived booleans for application code; excluded from the telemetry projection
const withDerivedFlags = (info) => ({
  ...info,
  isMobile: info.platform === "mobile",
  isTablet: info.platform === "tablet",
  isDesktop: info.platform === "desktop",
  // platform can also be "tv" or "unknown", where all three above are false
  isHandheld: info.platform === "mobile" || info.platform === "tablet",
  isIOS: info.os === "iOS",
  isMacOS: info.os === "macOS",
  isAndroid: info.os === "Android",
  isAppleDevice: info.os === "iOS" || info.os === "macOS",
  // Every iOS browser is WebKit; branch camera workarounds on this, not the browser key
  isWebKit: info.engine === "WebKit" || info.os === "iOS",
  isInAppBrowser: info.webViewHost !== null
})

// The cached object is shared by every consumer; freezing stops one mutating it for all
const deepFreeze = (info) => {
  Object.freeze(info.browserInfo)
  Object.freeze(info.features)
  return Object.freeze(info)
}

const safeUserAgent = (nav) => {
  try {
    return nav?.userAgent ?? null
  } catch {
    return null
  }
}

// An exception implies a cross-origin ancestor, which itself means framed
const resolveIsFramed = (win) => {
  try {
    return win.self !== win.top
  } catch {
    return true
  }
}

const probeFeatures = (nav, win) => ({
  localStorage: isStorageAvailable(win, "localStorage"),
  sessionStorage: isStorageAvailable(win, "sessionStorage"),
  touchEnabled: "ontouchstart" in win || nav.maxTouchPoints > 0,
  geolocationSupported: "geolocation" in nav,
  // Without these two, getUserMedia is unavailable regardless of permission state
  mediaDevices: Boolean(nav.mediaDevices?.getUserMedia),
  secureContext: win.isSecureContext === true
})

const safeProbeFeatures = (nav, win) => {
  try {
    return probeFeatures(nav, win)
  } catch {
    return {...DEFAULT_DEVICE_INFO.features}
  }
}

let cachedDeviceInfo = null

/** Test seam. */
export const resetDeviceInfoCache = () => {
  cachedDeviceInfo = null
}

/**
 * Parses the user agent and probes runtime capabilities, cached for the session.
 * nav/win parameters are a test seam.
 */
export const getDeviceInfo = ({nav = navigator, win = window} = {}) => {
  if (cachedDeviceInfo) return cachedDeviceInfo

  try {
    const userAgent = nav.userAgent
    const parser = Bowser.getParser(userAgent)
    const browser = parser.getBrowser()
    const os = parser.getOS()

    // iPadOS Safari masquerades as desktop macOS; touch support is the only reliable signal
    const isMasqueradingIPad = os.name === "macOS" && nav.maxTouchPoints > 1

    cachedDeviceInfo = deepFreeze(withDerivedFlags({
      ...DEFAULT_DEVICE_INFO,
      userAgent,
      platform: isMasqueradingIPad ? "tablet" : (parser.getPlatform().type || UNKNOWN),
      language: nav.language ?? null,
      os: isMasqueradingIPad ? "iOS" : (os.name || UNKNOWN),
      // Chromium freezes this via UA reduction, and a masquerading iPad's real version is unrecoverable
      osVersion: isMasqueradingIPad ? UNKNOWN : (os.version || UNKNOWN),
      engine: parser.getEngine().name || UNKNOWN,
      browserInfo: {
        key: resolveBrowserKey(parser.getBrowserName(true)),
        name: browser.name ?? null,
        version: browser.version ?? null,
        majorVersion: toMajorVersion(browser.version)
      },
      webViewHost: resolveWebViewHost(userAgent),
      isFramed: resolveIsFramed(win),
      features: probeFeatures(nav, win)
    }))

    return cachedDeviceInfo

  } catch (error) {
    Sentry.captureException(error, {tags: {module: "deviceInfo"}})
    // Cached like the success path, since the provider calls this every render.
    // Probes still run: all-false features would dead-end the journey.
    cachedDeviceInfo = deepFreeze(withDerivedFlags({
      ...DEFAULT_DEVICE_INFO,
      userAgent: safeUserAgent(nav),
      // Copied, not shared: deepFreeze would otherwise freeze the template for the process
      browserInfo: {...DEFAULT_DEVICE_INFO.browserInfo},
      isFramed: resolveIsFramed(win),
      features: safeProbeFeatures(nav, win),
      parseFailed: true
    }))
    return cachedDeviceInfo
  }
}

/**
 * Low-cardinality projection for Elastic; userAgent is excluded deliberately (high
 * cardinality, already parsed out). Tolerates a malformed input since it runs
 * synchronously inside the metric epic, where a throw would kill every epic for the session.
 */
export const toDeviceTelemetry = (info) => {
  const {browserInfo = {}, features = {}} = info ?? {}
  return {
    platform: info?.platform ?? UNKNOWN,
    os: info?.os ?? UNKNOWN,
    osVersion: info?.osVersion ?? UNKNOWN,
    engine: info?.engine ?? UNKNOWN,
    browserKey: browserInfo.key ?? BROWSER_KEYS.UNKNOWN,
    browserMajorVersion: browserInfo.majorVersion ?? null,
    webViewHost: info?.webViewHost ?? null,
    isFramed: info?.isFramed ?? false,
    storageAvailable: features.localStorage ?? false,
    secureContext: features.secureContext ?? false,
    mediaDevicesSupported: features.mediaDevices ?? false,
    deviceInfoParseFailed: info?.parseFailed ?? false
  }
}

/**
 * Emits only when a real fix is obtained. Denial, timeout, unavailable hardware and a
 * missing API all complete without emitting: the sole consumer POSTs the result to the
 * upload service, which rejects a body carrying no coordinates.
 *
 * Known defects, deliberately unfixed: the 5s timeout excludes the permission prompt
 * itself, and maximumAge: 0 forces a cold GPS fix. Check retention against POPIA before
 * relying on the full-precision coordinates.
 */
export const getUserLocation = () => defer(() => {
  if (!navigator.geolocation) {
    return EMPTY
  }

  const locationPromise = new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: parseFloat(position.coords.accuracy.toFixed(2)),
            geolocationSupported: true,
            locationPermissionGranted: true
          })
        },
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
    )
  })

  return from(locationPromise).pipe(
      filter(Boolean),
      catchError(error => {
        console.error('Error getting location:', error)
        return EMPTY
      })
  )
})